/**
 * UI component factories — overlays.
 */
import { registerComponent } from '../registryCore';
import { bool, getState, num, setParts, setState, str, syntheticEvent, } from '../helpers';
import { scheduleAutoDismiss, trapFocusIn, wireSelectFromList, } from '../interaction';
import { UI, createGroup } from './shared';
/** Tooltip — show on hover/focus */
registerComponent('tooltip', function (props, app) {
    var text = str(props, 'text', 'Tooltip');
    var anchor = str(props, 'anchor', 'Hover me');
    var placement = str(props, 'placement', 'bottom');
    var delay = num(props, 'delay', 0);
    var group = createGroup(app, 'tooltip', props, { visible: bool(props, 'visible', false), listening: true });
    var pad = 10;
    var tw = text.length * 7 + pad * 2;
    var bubbleY = placement === 'top' ? -36 : placement === 'right' ? 4 : 28;
    var bubbleX = placement === 'right' ? anchor.length * 7 + 12 : 0;
    var anchorText = app.text({
        text: anchor,
        fontSize: UI().fontSize,
        fill: UI().primary,
        x: 0,
        y: 4,
        listening: false,
    });
    var bg = app.roundedRect({
        width: tw,
        height: 32,
        cornerRadius: UI().radiusSm,
        fill: '#1e293b',
        shadow: UI().shadowMd,
        x: bubbleX,
        y: bubbleY,
        listening: false,
        visible: group.visible,
    });
    var label = app.text({
        text: text,
        fontSize: UI().fontSizeSm,
        fill: UI().textInverse,
        x: bubbleX + pad,
        y: bubbleY + 8,
        listening: false,
        visible: group.visible,
    });
    group.add(anchorText, bg, label);
    setParts(group, { anchor: anchorText, bg: bg, label: label });
    setState(group, { text: text, anchor: anchor, placement: placement, delay: delay, visible: group.visible });
    var delayTimer;
    var show = function () {
        var _a;
        group.visible = true;
        bg.visible = true;
        label.visible = true;
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        group.emit('open', syntheticEvent('open', group));
    };
    var hide = function () {
        var _a;
        if (delayTimer !== undefined)
            clearTimeout(delayTimer);
        group.visible = false;
        bg.visible = false;
        label.visible = false;
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        group.emit('close', syntheticEvent('close', group));
    };
    group.on('mouseenter', function () {
        if (delay <= 0)
            show();
        else
            delayTimer = setTimeout(show, delay);
    });
    group.on('mouseleave', hide);
    return group;
});
/** Menu / Dropdown */
registerComponent('menu', function (props, app) {
    var _a, _b, _c;
    var items = (_a = props.items) !== null && _a !== void 0 ? _a : ['Item 1', 'Item 2', 'Item 3'];
    var variants = (_b = props.itemVariants) !== null && _b !== void 0 ? _b : [];
    var open = bool(props, 'open', false);
    var rowH = 32;
    var width = num(props, 'width', 180);
    var height = Math.min(items.length * rowH + 12, 248);
    var group = createGroup(app, 'menu', props, {
        focusable: true,
        role: 'menu',
        visible: open,
        metadata: { componentType: 'menu', label: (_c = props.label) !== null && _c !== void 0 ? _c : 'Menu' },
    });
    var bg = app.roundedRect({
        width: width,
        height: height,
        cornerRadius: UI().radius,
        fill: UI().surface,
        stroke: UI().border,
        strokeWidth: 1,
        shadow: UI().shadowLg,
        listening: false,
    });
    group.add(bg);
    var isDanger = function (item, i) {
        return variants[i] === 'danger' ||
            ['delete', 'remove', 'danger'].includes(item.toLowerCase());
    };
    items.forEach(function (item, i) {
        group.add(app.text({
            text: item,
            x: 14,
            y: 10 + i * rowH,
            fontSize: UI().fontSize,
            fill: isDanger(item, i) ? UI().danger : UI().text,
            listening: false,
        }));
    });
    setParts(group, { bg: bg });
    setState(group, { items: items, open: open, width: width, selectedIndex: -1, triggerLabel: props.triggerLabel, itemVariants: variants });
    group.on('click', function (e) {
        var _a, _b;
        if (!group.visible) {
            group.visible = true;
            setState(group, { open: true });
            group.emit('open', syntheticEvent('open', group));
        }
        else {
            (_a = e.stopPropagation) === null || _a === void 0 ? void 0 : _a.call(e);
        }
        (_b = group.getApp()) === null || _b === void 0 ? void 0 : _b.requestRender();
    });
    wireSelectFromList(group, items, 'selectedIndex', function (index) {
        group.visible = false;
        setState(group, { open: false, selectedIndex: index });
    });
    return group;
});
/** Dialog / Modal */
registerComponent('dialog', function (props, app) {
    var width = num(props, 'width', 320);
    var height = num(props, 'height', 200);
    var open = bool(props, 'open', true);
    var title = str(props, 'title', 'Dialog');
    var group = createGroup(app, 'dialog', props, {
        focusable: true,
        role: 'dialog',
        visible: open,
        metadata: { componentType: 'dialog', label: title },
    });
    var overlay = app.rect({
        width: num(props, 'overlayWidth', 800),
        height: num(props, 'overlayHeight', 600),
        fill: UI().overlay,
        x: -num(props, 'x', 0),
        y: -num(props, 'y', 0),
        listening: true,
    });
    var panel = app.roundedRect({
        width: width,
        height: height,
        cornerRadius: UI().radiusLg,
        fill: UI().surface,
        stroke: UI().border,
        strokeWidth: 1,
        shadow: UI().shadowLg,
        x: 0,
        y: 0,
    });
    var titleText = app.text({
        text: title,
        fontSize: UI().fontSizeLg,
        fontWeight: 'bold',
        fill: UI().text,
        x: 20,
        y: 18,
    });
    var divider = app.rect({
        width: width - 40,
        height: 1,
        fill: UI().border,
        x: 20,
        y: 48,
        listening: false,
    });
    var bodyText = app.text({
        text: str(props, 'message', 'Are you sure you want to continue?'),
        fontSize: UI().fontSize,
        fill: UI().textSecondary,
        x: 20,
        y: 64,
        listening: false,
    });
    group.add(overlay, panel, titleText, divider, bodyText);
    setParts(group, { overlay: overlay, panel: panel, titleText: titleText });
    setState(group, {
        open: open,
        title: title,
        message: str(props, 'message', 'Are you sure you want to continue?'),
        width: width,
        height: height,
        overlayWidth: num(props, 'overlayWidth', 800),
        overlayHeight: num(props, 'overlayHeight', 600),
    });
    if (open)
        trapFocusIn(group);
    group.on('click', function () {
        var _a;
        if (!getState(group).open) {
            setState(group, { open: true });
            group.visible = true;
            trapFocusIn(group);
            group.emit('open', syntheticEvent('open', group));
            (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        }
    });
    overlay.on('click', function (e) {
        var _a;
        e.stopPropagation();
        setState(group, { open: false });
        group.visible = false;
        group.emit('close', syntheticEvent('close', group));
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
    });
    return group;
});
/** Toast / Notification */
registerComponent('toast', function (props, app) {
    var _a;
    var message = str(props, 'message', 'Notification');
    var variant = str(props, 'variant', 'success');
    var position = str(props, 'position', '');
    var dismissible = bool(props, 'dismissible', true);
    var duration = num(props, 'duration', 3000);
    var fills = {
        success: UI().surfaceInset,
        error: UI().surfaceInset,
        warning: UI().surfaceInset,
        info: UI().primaryMuted,
    };
    var group = createGroup(app, 'toast', props, {
        role: 'status',
        ariaLive: 'polite',
        metadata: { componentType: 'toast', ariaLive: 'polite' },
    });
    var tw = Math.max(message.length * 7 + 32, 160);
    var bg = app.roundedRect({
        width: tw,
        height: 40,
        cornerRadius: UI().radius,
        fill: (_a = fills[variant]) !== null && _a !== void 0 ? _a : fills.success,
        shadow: UI().shadowLg,
        listening: false,
    });
    var textNode = app.text({
        text: message,
        fontSize: UI().fontSize,
        fill: UI().textInverse,
        x: 16,
        y: 11,
        listening: false,
    });
    group.add(bg, textNode);
    setParts(group, { bg: bg, text: textNode });
    setState(group, { message: message, duration: duration, variant: variant, position: position, dismissible: dismissible });
    group.emit('open', syntheticEvent('open', group));
    scheduleAutoDismiss(group, duration, function () {
        group.visible = false;
    });
    return group;
});
